import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FacultyLayout from './FacultyLayout';
import {
  ArrowLeft,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Edit,
  Loader2,
  X,
  Download,
} from 'lucide-react';
import { submissionsAPI, resolveServerUrl } from '../../services/api';
import mammoth from 'mammoth';

interface SubmissionDetail {
  id: number;
  title: string;
  category_name: string;
  description: string;
  status: string;
  submitted_date: string;
  reviewed_date?: string;
  reviewed_by?: string;
  feedback?: string;
  file_name?: string;
  file_path?: string;
  file_size?: number;
}

export default function SubmissionDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [docxPreviewHtml, setDocxPreviewHtml] = useState<string>('');
  const [docxPreviewLoading, setDocxPreviewLoading] = useState(false);
  const [docxPreviewError, setDocxPreviewError] = useState('');

  useEffect(() => {
    const fetchSubmission = async () => {
      if (!id) return;
      try {
        const result = await submissionsAPI.getById(parseInt(id));
        if (!result.error && result.data) {
          setSubmission(result.data);
        } else {
          setError(result.message || 'Failed to load submission');
        }
      } catch (err: any) {
        console.error('Failed to fetch submission:', err);
        setError(err.message || 'Failed to load submission');
      } finally {
        setLoading(false);
      }
    };
    fetchSubmission();
  }, [id]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-5 h-5 mr-2" />
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
            <Clock className="w-5 h-5 mr-2" />
            Pending Review
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-700">
            <XCircle className="w-5 h-5 mr-2" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fileUrl = submission?.file_path ? resolveServerUrl(submission.file_path) : '';

  const getFileExtension = () => {
    const source = submission?.file_name || submission?.file_path || '';
    const dotIndex = source.lastIndexOf('.');
    if (dotIndex < 0) return '';
    return source.slice(dotIndex + 1).toLowerCase();
  };

  const fileExt = getFileExtension();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExt);
  const isPdf = fileExt === 'pdf';
  const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExt);
  const isAudio = ['mp3', 'wav', 'ogg', 'm4a'].includes(fileExt);
  const isDocx = fileExt === 'docx';

  useEffect(() => {
    const generateDocxPreview = async () => {
      if (!showPreview || !isDocx || !submission?.file_path) {
        setDocxPreviewHtml('');
        setDocxPreviewError('');
        setDocxPreviewLoading(false);
        return;
      }

      try {
        setDocxPreviewLoading(true);
        setDocxPreviewError('');
        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxPreviewHtml(result.value);
      } catch (err) {
        console.error('Failed to load DOCX preview:', err);
        setDocxPreviewHtml('');
        setDocxPreviewError('Preview not available for this DOCX file.');
      } finally {
        setDocxPreviewLoading(false);
      }
    };

    generateDocxPreview();
  }, [showPreview, isDocx, submission?.file_path, fileUrl]);

  if (loading) {
    return (
      <FacultyLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </FacultyLayout>
    );
  }

  if (error || !submission) {
    return (
      <FacultyLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <button
            onClick={() => navigate('/faculty/submissions')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Submissions
          </button>
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <p className="text-red-600">{error || 'Submission not found'}</p>
          </div>
        </div>
      </FacultyLayout>
    );
  }

  return (
    <FacultyLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/faculty/submissions')}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Submissions
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                {submission.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-4">
                <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                  {submission.category_name}
                </span>
                <span className="inline-flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Submitted: {new Date(submission.submitted_date).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="mt-4 sm:mt-0">{getStatusBadge(submission.status)}</div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
            <p className="text-gray-700 leading-relaxed">{submission.description}</p>
          </div>
        </div>

        {/* File Information */}
        {submission.file_name && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Submitted File</h3>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">{submission.file_name}</div>
                  <div className="text-sm text-gray-600">{formatFileSize(submission.file_size)}</div>
                </div>
              </div>
              {submission.file_path && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPreview(true)}
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </button>
                  <a
                    href={fileUrl}
                    download={submission.file_name || true}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {showPreview && submission?.file_path && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
          >
            <div
              className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">{submission.file_name || 'File Preview'}</h3>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
                {isImage ? (
                  <img src={fileUrl} alt={submission.file_name} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
                ) : isPdf ? (
                  <iframe src={fileUrl} title={submission.file_name} className="w-full h-[70vh] rounded-lg border border-gray-200" />
                ) : isVideo ? (
                  <video controls className="max-w-full max-h-[70vh] rounded-lg">
                    <source src={fileUrl} />
                    Your browser does not support the video tag.
                  </video>
                ) : isAudio ? (
                  <audio controls className="w-full">
                    <source src={fileUrl} />
                  </audio>
                ) : isDocx && !docxPreviewLoading && !docxPreviewError ? (
                  <div className="w-full h-[70vh] overflow-auto rounded-lg border border-gray-200 p-6 bg-white prose max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: docxPreviewHtml }} />
                  </div>
                ) : isDocx && docxPreviewLoading ? (
                  <div className="text-gray-600">Loading DOCX preview...</div>
                ) : (
                  <div className="text-center space-y-4 py-12">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                      <FileText className="w-12 h-12 text-gray-400" />
                    </div>
                    <p className="text-gray-600">
                      {docxPreviewError || 'Preview not available for this file type.'}
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <a
                        href={fileUrl}
                        download={submission.file_name || true}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Review Information */}
        {submission.reviewed_date && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Review Details</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-sm text-gray-600 mb-1">Review Date</div>
                <div className="font-medium text-gray-900">
                  {new Date(submission.reviewed_date).toLocaleDateString()}
                </div>
              </div>
            </div>

            {submission.feedback && (
              <div
                className={`p-4 rounded-lg ${
                  submission.status === 'approved'
                    ? 'bg-green-50 border border-green-200'
                    : submission.status === 'rejected'
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <p className="font-semibold text-gray-900 mb-2">Administrator Feedback:</p>
                <p
                  className={`${
                    submission.status === 'approved'
                      ? 'text-green-700'
                      : submission.status === 'rejected'
                      ? 'text-red-700'
                      : 'text-gray-700'
                  }`}
                >
                  {submission.feedback}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {submission.status === 'rejected' && (
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/faculty/submit"
              className="flex-1 inline-flex items-center justify-center bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-semibold"
            >
              <Edit className="w-5 h-5 mr-2" />
              Resubmit Work
            </Link>
          </div>
        )}
      </div>
    </FacultyLayout>
  );
}
